import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, type Language, type TranslationKey } from '@/lib/i18n';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKey;
  isRTL: boolean;
}

export const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: () => {},
  t: translations.en,
  isRTL: false,
});

export function useLanguageProvider() {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem('flexaura_language');
      return (stored === 'ar' ? 'ar' : 'en') as Language;
    } catch {
      return 'en';
    }
  });

  const isRTL = language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    localStorage.setItem('flexaura_language', language);
  }, [language, isRTL]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  return {
    language,
    setLanguage,
    t: translations[language],
    isRTL,
  };
}

export function useLanguage() {
  return useContext(LanguageContext);
}
